import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/Button';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    errorMessage: '',
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error.message };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);

    // Vite chunk load error 확인 (동적 임포트 실패 혹은 text/html 반환)
    const isChunkLoadFailed =
      error.name === 'TypeError' &&
      (error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Importing a module script failed') ||
        error.message.includes('MIME type of "text/html"'));

    if (isChunkLoadFailed) {
      // Chunk 에러 시 하얀화면 방지를 위해 SPA 전체 강제 리로딩 수행
      window.location.reload();
    }
  }

  public render() {
    if (this.state.hasError) {
      // Chunk 에러 외의 원인으로 화면이 크래시 났을 경우 보여줄 Fallback UI
      return (
        <div
          className='ap-bg-grey-100'
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            minHeight: '100vh',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div
            className='ap-bg-white'
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              maxWidth: '500px',
              width: '100%',
              padding: '32px',
              borderRadius: '16px',
              border: '1px solid var(--color-grey-200)',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            }}
          >
            <h1 className='ap-color-grey-800' style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>
              페이지를 불러올 수 없습니다
            </h1>
            <p className='ap-color-grey-600' style={{ fontSize: '16px', marginBottom: '24px', lineHeight: '1.5' }}>
              일시적인 네트워크 오류이거나 시스템 업데이트 중일 수 있습니다.
              <br /> 문제가 지속되면 관리자에게 문의해주세요.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', width: '100%' }}>
              <Button
                variant='primary'
                onClick={() => {
                  window.location.href = '/';
                }}
                style={{ padding: '0 24px', flex: 1 }}
              >
                홈으로 돌아가기
              </Button>
              <Button
                variant='outline'
                onClick={() => {
                  window.location.reload();
                }}
                style={{ padding: '0 24px', flex: 1 }}
              >
                다시 시도
              </Button>
            </div>
            {process.env.NODE_ENV === 'development' && (
              <div
                className='ap-bg-warning-100 ap-color-warning'
                style={{
                  marginTop: '32px',
                  padding: '16px',
                  borderRadius: '8px',
                  textAlign: 'left',
                  overflow: 'auto',
                  fontSize: '14px',
                  width: '100%',
                }}
              >
                <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>[개발자 전용 에러 로그]</p>
                <p>{this.state.errorMessage}</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
